import styled from '@emotion/styled';
import { Container, Dropdown, DropdownProps, Grid, Input, SemanticCOLORS } from 'semantic-ui-react';
import { Text } from '@britishcouncil/react-admin';

type Value = number | string;

type Item = {
  value: Value;
  text: string;
};

type Group = {
  title: string;
  color?: SemanticCOLORS;
  data: Item[];
};

export type MultipleColumnDropdownProps = Omit<DropdownProps, 'value' | 'onChange' | 'options'> & {
  options: Group[];
  value: Value;
  onChange: (value: Value) => void;
  onBlur?: () => void;
  error?: boolean;
  name: string;
  placeholder?: string;
};

export const MultipleColumnDropdown = ({
  value,
  onBlur,
  onChange,
  options,
  error,
  name,
  placeholder,
  ...dropdownProps
}: MultipleColumnDropdownProps) => {
  const selectedItem = options.flatMap((group) => group.data).find((item) => item.value === value);

  return (
    <S.Dropdown
      fluid
      floating
      onBlur={onBlur}
      trigger={
        <Input
          name={name}
          value={selectedItem?.text || ''}
          error={error}
          placeholder={placeholder}
        />
      }
      icon={null}
      error={error}
      {...dropdownProps}
    >
      <S.Menu>
        <Container fluid>
          <S.Grid columns={'equal'} divided>
            <Grid.Row>
              {options.map((dataGroup) => (
                <S.Column
                  key={dataGroup.title}
                  as={Grid}
                  data-testid={`multiple-column-dropdown-group-${dataGroup.title.toLowerCase()}`}
                >
                  <Grid.Row>
                    <Grid.Column>
                      <S.ColumnTitle color={dataGroup.color}>{dataGroup.title}</S.ColumnTitle>
                    </Grid.Column>
                  </Grid.Row>
                  {dataGroup.data.map((item) => (
                    <S.ItemRow
                      data-testid="multiple-column-dropdown-item"
                      key={item.value}
                      onClick={() => onChange(item.value)}
                      className={item.value === selectedItem?.value ? 'active' : ''}
                    >
                      <Grid.Column>
                        <S.WrappedText>{item.text}</S.WrappedText>
                      </Grid.Column>
                    </S.ItemRow>
                  ))}
                </S.Column>
              ))}
            </Grid.Row>
          </S.Grid>
        </Container>
      </S.Menu>
    </S.Dropdown>
  );
};

const S = {
  Dropdown: styled(Dropdown)`
    width: 100%;
  `,
  Menu: styled(Dropdown.Menu)`
    .ui.dropdown &.menu {
      width: fit-content;
      right: 0;
      left: auto;
      max-height: 21.37142857rem;
      overflow-x: hidden;
      overflow-y: auto;
    }
  `,
  Grid: styled(Grid)`
    width: 45vw;
  `,
  Column: styled(Grid.Column)`
    display: flex;
    align-items: center;
  `,
  ColumnTitle: styled(Text)`
    text-transform: uppercase;
    padding-left: 1.5rem;
    display: block;
  `,
  ItemRow: styled(Grid.Row)`
    .ui.grid > &.row {
      color: black;
      padding-left: 1.5rem;
      padding-right: 1.5rem;
    }

    &.active {
      border-left: 2px darkgreen solid;
      background: rgb(105, 255, 155);
      background: linear-gradient(90deg, rgba(105, 255, 155, 1) 0%, rgba(255, 255, 255, 0) 50%);

      &:hover > * {
        margin-left: 0px;
      }
    }

    &:hover {
      border-left: 2px darkblue solid;
      background: rgb(105, 173, 255);
      background: linear-gradient(90deg, rgba(105, 173, 255, 1) 0%, rgba(255, 255, 255, 0) 50%);
      cursor: pointer;

      > * {
        margin-left: -2px;
      }
    }
  `,
  WrappedText: styled('span')`
    white-space: normal;
  `,
};
